import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { FirestorePathsService } from './firestorePaths.service';
import { DateService } from './date.service';
import { FirestoreService } from './firestore.service';
import { SessionService } from './session.service';

// import { PickupQueueService } from './pickupQueue.service';
import type { ITicket, TTicket, TTicketStatus } from '@shared/models';
import { Constants } from '@shared/models';
const DateTimeFormats = Constants.DateTimeFormats;

@Injectable({
    'providedIn': 'root'
})
export class TicketsService {
    // private firebaseFunctionsService: FirebaseFunctionsService, private events: Events
    // private utilsService: UtilsService, private pickupQueueService: PickupQueueService,
    constructor(private firestoreService: FirestoreService, private firestorePathsService: FirestorePathsService,
        private sessionService: SessionService, private dateService: DateService) {
    }

    public getObservable(): Observable<TTicket[]> {
        const path = this.firestorePathsService.getTicketsPath();
        // const observable = this.firestoreService.getObservable(path).valueChanges();
        const observable = this.firestoreService.getObservable(path);
        return observable as Observable<TTicket[]>;
    }

    public async getByUid(uid: string): Promise<any> {
        const path = this.firestorePathsService.getTicketsPath();
        return this.firestoreService.getByUid(path, uid);
    }

    // public async getByFieldValue(fieldName, fieldValue): Promise<TTicket[]> {
    //     const path = this.firestorePathsService.getTicketsPath();
    //     return this.firestoreService.getByFieldValue(path, fieldName, fieldValue) as Promise<TTicket[]>;
    // }

    public async getByFieldValue<K extends keyof TTicket>(
        fieldName: K,
        fieldValue: TTicket[K]
    ): Promise<TTicket[]> {
        const path = this.firestorePathsService.getTicketsPath();
        return this.firestoreService.getByFieldValue(path, fieldName, fieldValue) as Promise<TTicket[]>;
    }

    public async getAll(): Promise<TTicket[]> {
        const path = this.firestorePathsService.getTicketsPath();
        return this.firestoreService.getAll(path) as Promise<TTicket[]>;
    }

    public async add(ticket: TTicket): Promise<TTicket> {
        const path = this.firestorePathsService.getTicketsPath();
        return this.firestoreService.add(path, ticket) as Promise<TTicket>;
    }

    public async set(ticket: TTicket): Promise<TTicket> {
        const path = this.firestorePathsService.getTicketsPath();
        return this.firestoreService.set(path, ticket) as Promise<TTicket>;
    }

    public async update(ticket: TTicket): Promise<any> {
        try {
            const path = this.firestorePathsService.getTicketsPath();
            return this.firestoreService.update(path, ticket);
        } catch (err) {
            // console .log('TicketsService.update(); Error: message', err.message);
        }
    }

    public async delete(uid: string): Promise<void> {
        const collection = this.firestorePathsService.getTicketsPath();
        return this.firestoreService.delete(collection, uid);
    }

    //
    public async getPendingTickets(): Promise<TTicket[]> {
        return this.getTicketsByStatus('pending');
    }

    public async getAssignedTickets(): Promise<TTicket[]> {
        return this.getTicketsByStatus('assigned');
    }

    public async getEnrouteTickets(): Promise<TTicket[]> {
        return this.getTicketsByStatus('enroute');
    }

    public async getCurrentTickets(): Promise<TTicket[]> {
        return this.getTicketsByStatus('current');
    }

    public async getCompletedTickets(): Promise<TTicket[]> {
        return this.getTicketsByStatus('completed');
    }

    private async getTicketsByStatus(status: TTicketStatus): Promise<TTicket[]> {
        const collection = this.firestorePathsService.getTicketsPath();
        return this.firestoreService.getByFieldValue(collection, 'status', status) as Promise<TTicket[]>;
    }

    public async updateTicketsToAssigned(tickets: TTicket[]): Promise<TTicket[]> {
        const promises: Promise<TTicket>[] = [];

        tickets.forEach(async (ticket) => {
            ticket.status = 'assigned';
            ticket.assignedDate = this.dateService.now();
            ticket.assignedDateStr = this.dateService.formatTimestamp(ticket.assignedDate, DateTimeFormats.LongDate);
            promises.push(this.update(ticket));
        });
        // return Promise.all(promises);
        await Promise.all(promises);
        return tickets;   // make certain the tickets that are returned have a status of assigned.
    }

    public async updateTicketToPending(ticketUid: string): Promise<TTicket> {
        return this.updateTicketStatus(ticketUid, 'pending');
    }

    public async updateTicketToAssigned(ticketUid: string): Promise<TTicket> {
        return this.updateTicketStatus(ticketUid, 'assigned');
    }

    // While a passenger is enroute
    public async updateTicketToEnroute(ticketUid: string): Promise<TTicket> {
        return this.updateTicketStatus(ticketUid, 'enroute');
    }

    public async updateTicketToCurrent(ticketUid: string): Promise<TTicket> {
        return this.updateTicketStatus(ticketUid, 'current');
    }

    public async updateTicketToCompleted(ticketUid: string): Promise<TTicket> {
        return this.updateTicketStatus(ticketUid, 'completed');
    }

    private async updateTicketStatus(ticketUid: string, status: TTicketStatus): Promise<TTicket> {
        const ticket = await this.getByUid(ticketUid);
        if (ticket) {
            ticket.status = status;
            const now = this.dateService.now();
            switch (ticket.status) {
                case 'assigned':
                    ticket.assignedDate = now;
                    break;

                case 'enroute':
                    ticket.enrouteDate = now;
                    ticket.driver = this.sessionService.getUsersFullName;
                    break;

                case 'current':
                    ticket.currentDate = now;
                    break;

                case 'completed':
                    ticket.completedDate = now;
                    break;
            }
            const tick = await this.update(ticket);
            return tick;
        }

        throw new Error('Ticket not found for uid: ' + ticketUid);
    }

    // Remove all the completed tickets for this shuttle.
    public async deleteCompletedTickets(tickets: TTicket[]): Promise<void> {
        if (!tickets || tickets.length === 0) {
            return Promise.resolve();
        }

        const completedTickets = tickets.filter((ticket) => (ticket.status === 'completed'));
        return this.deleteTickets(completedTickets);
    }

    public async deleteTickets(tickets: TTicket[]): Promise<void> {
        if (!tickets || tickets.length === 0) {
            return Promise.resolve();
        }

        const promises: Promise<ITicket | void>[] = [];
        tickets.forEach((ticket) => {
            const p = this.delete(ticket.uid);
            promises.push(p);
        });
        await Promise.all(promises);
        return;
    }

    public sortPendingTickets(tickets: TTicket[]): TTicket[] {
        return this.sort(tickets, 'createdDate');
    }

    public sortAssignedTickets(tickets: TTicket[]): TTicket[] {
        return this.sort(tickets, 'assignedDate');
    }

    public sortEnrouteTickets(tickets: TTicket[]): TTicket[] {
        return this.sort(tickets, 'enrouteDate');
    }

    public sortCurrentTickets(tickets: TTicket[]): TTicket[] {
        return this.sort(tickets, 'currentDate');
    }

    public sortCompletedTickets(tickets: TTicket[]): TTicket[] {
        return this.sort(tickets, 'completedDate');
    }

    // private sort(tickets: TTicket[], sortColumn: string): TTicket[] {
    //     tickets.sort((a: TTicket, b: TTicket) => {
    //         // if (a.shuttleName < b.shuttleName) {
    //         if (a[sortColumn] < b[sortColumn]) {
    //             return -1;
    //         } else if (a[sortColumn] > b[sortColumn]) {
    //             return 1;
    //         } else {
    //             return 0;
    //         }
    //     });
    //     return tickets;
    // }

    private sort<K extends keyof TTicket>(tickets: TTicket[], sortColumn: K): TTicket[] {
        tickets.sort((a: TTicket, b: TTicket) => {
            const aValue = a[sortColumn];
            const bValue = b[sortColumn];
            
            // Handle undefined/null values - put them at the end
            if (aValue === undefined || aValue === null) return 1;
            if (bValue === undefined || bValue === null) return -1;

            if (aValue < bValue) {
                return -1;
            } else if (aValue > bValue) {
                return 1;
            } else {
                return 0;
            }
        });
        return tickets;
    }    

    // This routine is only for debugging the pickup request functionality.
    // #shuttle-does-not-exist-on-type-IVector
    // public async debugAddPickupRequest() {
    // By no means do these tasks need to be done sequentially.
    // However, the adding the ticket to the pickup request queue needs to be done last!
    // const tickets = await this.getAll();
    // tickets.forEach(async (tick: TTicket) => {
    //     if (tick.locationName === 'Pickup Request') {
    //         await this.delete(tick.uid);
    //     }
    // });

    // const vector = vectors.find((v: TVector) => (v.shuttle.shuttleName === 'ShuttleForDebugging'));

    // Dealership Coordinates = 35.9657247, -115.0984695.  Offset the coordinates to simulate out driving.
    // https://gps-coordinates.org/distance-between-coordinates.php
    // vector.currentLocation.latitude = 36.0;
    // vector.currentLocation.longitude = -115.0;
    // vector.distanceFromDealership = { 'miles': 5.99, 'kilometers': 9.65 };

    // const ticket: TTicket = {};

    // ticket.passengerName = 'Pickup Request At CostCo';
    // ticket.fromDealership = false;
    // ticket.status = 'pending';
    // ticket.numPassengers = 1;
    // ticket.locationName = 'Pickup Request';
    // ticket.streetAddress = '3411 Saint Rose Pkwy';
    // ticket.fromAddress = '3411 Saint Rose Pkwy, Henderson, NV 89052';
    // ticket.fromcoordinate = { 'latitude': 36.0, 'longitude': -115.0 };
    // ticket.tocoordinate = this.sessionService.getDealership().address.coordinate;
    // ticket.cellPhone = '6618778631';
    // ticket.formattedCellPhone = this.utilsService.formatPhoneNumberForDisplay(ticket.cellPhone);
    // ticket.priority = false;
    // ticket.note = 'Gate code is 1234';
    // ticket.priorityStr = 'Non-Urgent';
    // ticket.pickup = {
    //     'locationName': ticket.locationName,
    //     'address': ticket.fromAddress,
    //     // 'streetAddress': ticket.streetAddress,
    //     'city': 'Henderson',
    //     'zipcode': '89052',
    //     'coordinate': ticket.fromcoordinate,
    // };
    // const tix = await this.add(ticket);
    // await this.pickupQueueService.initialize();
    // this.pickupQueueService.addTicketToPickupQueue(tix);
    // }

    // this.vm.ticket.pickup.streetAddress = this.utilsService.replaceCh(this.vm.ticket.pickup.streetAddress, '.#', '');

    // this.vm.ticket.pickup.address = this.utilsService.formatAddressForGoogleMaps(
    //     this.vm.ticket.pickup.streetAddress, this.vm.ticket.pickup.city, this.state,
    //     this.vm.ticket.pickup.zipcode, this.countryName);


    // public getTicketImageSource(status) {
    //     let imageSrc = null;

    //     switch (status) {
    //         case 'pending':
    //             imageSrc = Constants.PENDING_TICKET_IMAGE;
    //             break;

    //         case 'assigned':
    //             imageSrc = Constants.ASSIGNED_TICKET_IMAGE;
    //             break;

    //         case 'enroute':
    //             imageSrc = Constants.ENROUTE_TICKET_IMAGE;
    //             break;

    //         case 'current':
    //             imageSrc = Constants.CURRENT_TICKET_IMAGE;
    //             break;

    //         case 'completed':
    //             imageSrc = Constants.COMPLETED_TICKET_IMAGE;
    //             break;
    //     }

    //     return imageSrc;
    // }

    public async setAllTicketsToPending(): Promise<void> {
        const promises: Promise<ITicket>[] = [];
        const tickets = await this.getAll();
        tickets.forEach((ticket: TTicket) => {
            const p = this.setTicketToPending(ticket);
            promises.push(p);
        });
        await Promise.all(promises);
    }

    public async setTicketToPending(ticket: TTicket): Promise<TTicket> {
        delete ticket.assignedDate;
        delete ticket.enrouteDate;
        delete ticket.currentDate;
        delete ticket.completedDate;
        ticket.createdDate = this.dateService.toTimestamp(Date.now());
        ticket.status = 'pending';
        return this.update(ticket);
    }

    public async getTicketsByUids(ticketUids: string[]): Promise<TTicket[]> {
        const tickets: TTicket[] = [];
        for (const ticketUid of ticketUids) {
            const ticket = await this.getByUid(ticketUid);
            tickets.push(ticket);
        }

        return tickets;
    }
}
